<script context="module" lang="ts">
    // @hotwired/turbo ships no type declarations; window.Turbo is set up globally by the app entrypoint.
    declare const window: Window & {
        Turbo: { renderStreamMessage: (html: string) => void };
    };
</script>

<script lang="ts">
    import dayjs from 'dayjs';
    import { onDestroy, onMount } from 'svelte';

    import { filtersContext } from '$saga/views/Search/store';
    import { Sorting, type SearchForm } from '$saga/views/Search/types';

    export let searchForm: SearchForm;
    export let searchUrl: string;

    /**
     * Refresh the URL with the update added/remove filters in query params
     */
    const handleQueryParamsUpdate = () => {
        $filtersContext.isSubmitting = false;

        // update the URL sorting parameter to match the current search results,
        // read from our own prop rather than the separate `sortValue` store
        // (which is updated by a different component and can lag on some flows,
        // e.g. after browser back/forward navigation).
        // eslint-disable-next-line svelte/prefer-svelte-reactivity -- local, only used to build a URL string
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set('sort', searchForm.sort ?? Sorting.DEFAULT);

        urlParams.delete('destination[]');
        if (searchForm.destinations) {
            searchForm.destinations?.forEach((dest) =>
                urlParams.append('destination[]', String(dest)),
            );
        }

        urlParams.delete('theme[]');
        if (searchForm.sports) {
            searchForm.sports.forEach((theme) =>
                urlParams.append('theme[]', String(theme)),
            );
        }

        urlParams.delete('startDate');
        if (searchForm.startDate) {
            urlParams.set(
                'startDate',
                dayjs(searchForm.startDate.date).format('DD/MM/YYYY'),
            );
        }

        urlParams.delete('endDate');
        if (searchForm.endDate) {
            urlParams.set(
                'endDate',
                dayjs(searchForm.endDate.date).format('DD/MM/YYYY'),
            );
        }

        urlParams.delete('month[]');
        if (searchForm.months) {
            searchForm.months.forEach((month) =>
                urlParams.append('month[]', String(month)),
            );
        }

        urlParams.delete('flexibility');
        if (searchForm.flexibility) {
            urlParams.set('flexibility', searchForm.flexibility.toString());
        }

        urlParams.delete('nbPax');
        if (searchForm.nbPax) {
            urlParams.set('nbPax', searchForm.nbPax.toString());
        }

        urlParams.delete('level[]');
        if (searchForm.levels) {
            searchForm.levels.forEach((level) =>
                urlParams.append('level[]', level),
            );
        }

        urlParams.delete('type[]');
        if (searchForm.types) {
            searchForm.types.forEach((type) =>
                urlParams.append('type[]', type),
            );
        }

        urlParams.delete('durationMin');
        urlParams.delete('durationMax');
        if (
            searchForm.durationMin !== null &&
            searchForm.durationMin !== $filtersContext.defaultDurationMin
        ) {
            urlParams.set('durationMin', searchForm.durationMin.toString());
        }
        if (
            searchForm.durationMax !== null &&
            searchForm.durationMax !== $filtersContext.defaultDurationMax
        ) {
            urlParams.set('durationMax', searchForm.durationMax.toString());
        }

        urlParams.delete('budgetMin');
        if (
            searchForm.budgetMin !== null &&
            searchForm.budgetMin !== $filtersContext.defaultBudgetMin
        ) {
            urlParams.set('budgetMin', searchForm.budgetMin.toString());
        }

        urlParams.delete('budgetMax');
        if (
            searchForm.budgetMax !== null &&
            searchForm.budgetMax !== $filtersContext.defaultBudgetMax
        ) {
            urlParams.set('budgetMax', searchForm.budgetMax.toString());
        }

        urlParams.delete('tag[]');
        if (searchForm.tags) {
            searchForm.tags?.forEach((tag) => urlParams.append('tag[]', tag));
        }

        urlParams.delete('resort[]');
        if (searchForm.resorts) {
            searchForm.resorts.forEach((resort) =>
                urlParams.append('resort[]', String(resort)),
            );
        }

        // Admin fields
        const adminBookingTypes = document.querySelectorAll<HTMLInputElement>(
            'input[name="bookingTypes[]"]:checked',
        );
        urlParams.delete('bookingTypes[]');
        if (adminBookingTypes.length > 0) {
            adminBookingTypes.forEach((input) => {
                urlParams.append('bookingTypes[]', input.value);
            });
        }

        const adminBrands = document.querySelectorAll<HTMLInputElement>(
            'input[name="brands[]"]:checked',
        );
        urlParams.delete('brands[]');
        if (adminBrands.length > 0) {
            adminBrands.forEach((input) => {
                urlParams.append('brands[]', input.value);
            });
        }

        const adminStatus = document.querySelectorAll<HTMLInputElement>(
            'input[name="status[]"]:checked',
        );
        urlParams.delete('status[]');
        if (adminStatus.length > 0) {
            adminStatus.forEach((input) => {
                urlParams.append('status[]', input.value);
            });
        }

        const minAgeLowerBound = document.getElementById(
            'minAgeLowerBound',
        ) as HTMLInputElement | null;
        urlParams.delete('minAgeLowerBound');
        if (minAgeLowerBound && minAgeLowerBound.value) {
            urlParams.set('minAgeLowerBound', minAgeLowerBound.value);
        }

        const maxAgeUpperBound = document.getElementById(
            'maxAgeUpperBound',
        ) as HTMLInputElement | null;
        urlParams.delete('maxAgeUpperBound');
        if (maxAgeUpperBound && maxAgeUpperBound.value) {
            urlParams.set('maxAgeUpperBound', maxAgeUpperBound.value);
        }

        const limitedAgeMin = document.getElementById(
            'limitedAgeMin',
        ) as HTMLInputElement | null;
        urlParams.delete('limitedAgeMin');
        if (limitedAgeMin && limitedAgeMin.value) {
            urlParams.set('limitedAgeMin', limitedAgeMin.value);
        }

        const limitedAgeMax = document.getElementById(
            'limitedAgeMax',
        ) as HTMLInputElement | null;
        urlParams.delete('limitedAgeMax');
        if (limitedAgeMax && limitedAgeMax.value) {
            urlParams.set('limitedAgeMax', limitedAgeMax.value);
        }

        urlParams.delete('keywords');
        if (searchForm.keywords) {
            urlParams.set('keywords', searchForm.keywords);
        }

        const bestReviewed = document.getElementById(
            'bestReviewed',
        ) as HTMLInputElement | null;
        urlParams.delete('bestReviewed');
        if (bestReviewed && bestReviewed.value) {
            urlParams.set('bestReviewed', bestReviewed.value);
        }

        const exclusiveSport = document.getElementById(
            'exclusiveSport',
        ) as HTMLInputElement | null;
        urlParams.delete('exclusiveSport');
        if (exclusiveSport && exclusiveSport.value) {
            urlParams.set('exclusiveSport', exclusiveSport.value);
        }

        const newRelativePathQuery = `${searchUrl}?${urlParams.toString()}`;
        history.pushState(null, '', newRelativePathQuery);
    };

    /**
     * Browser back/forward navigation only restores the URL (it never fires a request):
     * our own history entries come from plain history.pushState above, and Turbo Drive
     * is disabled app-wide, so nothing re-fetches the page on its own.
     * Replay the restored URL as the same kind of Turbo Stream request a regular
     * filter change triggers, so results/badges/header are refreshed identically.
     */
    const handlePopState = async () => {
        const response = await fetch(window.location.href, {
            headers: { Accept: 'text/vnd.turbo-stream.html' },
        });
        if (response.ok) {
            window.Turbo.renderStreamMessage(await response.text());
        }
    };

    /**
     * As component is being remounted on each Turbo Stream update,
     * we need to skip the URL update only on the very first page load.
     * After that, all subsequent Turbo Stream remounts should update the URL.
     */
    onMount(() => {
        handleQueryParamsUpdate();
        const skeleton = document.getElementById(
            'result-cards-skeleton-container',
        );

        if (skeleton && !skeleton.classList.contains('hidden')) {
            skeleton.classList.add('hidden');
        }
        window.addEventListener('popstate', handlePopState);
    });

    onDestroy(() => {
        window.removeEventListener('popstate', handlePopState);
    });
</script>
